import { Button, Tooltip, TooltipContent, TooltipTrigger } from "@istari-frontend/ui";
import { ArrowUpIcon, Check, FileIcon, PlusIcon, XIcon } from "lucide-react";
import { type FormEvent, type KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router";

import { useAiChat } from "~/ai-chat/feature/use-ai-chat";
import { buildReferencedFileName } from "~/ai-chat/model/chat-message";
import { useUploadResource } from "~/resource/data-source/use-upload-resource";
import { routes } from "~/routes";
import { AddFilesModal } from "~/upload/feature/add-files-modal";
import UploadSpecModal from "~/upload/ui/upload-spec-modal";
import { useFileUpload } from "~/upload/use-file-upload";
import { cn } from "~/utils/cn";

interface AiChatComposerProps {
  onSend: (content: string) => void;
  isPending: boolean;
}

export function AiChatComposer({ onSend, isPending }: AiChatComposerProps) {
  const [draft, setDraft] = useState("");
  const { referencedFiles, addReferencedFiles, removeReferencedFile, clearReferencedFiles } = useAiChat();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [addFilesModalOpen, setAddFilesModalOpen] = useState(false);
  const textareaId = useId();

  // Upload confirmation lives inside the chat panel (not a global toast) so it appears in the same
  // window as the chat — including the popped-out floating window, which a root-level sonner toast
  // can't reach — and clears itself, since the file chips below are the lasting confirmation.
  const [addedNotice, setAddedNotice] = useState<string | null>(null);
  const addedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (addedTimerRef.current) clearTimeout(addedTimerRef.current);
    },
    [],
  );
  const trimmed = draft.trim();
  const canSend = trimmed.length > 0 && !isPending;

  const { uploadFiles } = useUploadResource({
    onComplete: ({ files }) => {
      const uploaded = files.flatMap((file) =>
        file.state === "success" && file.resourceId
          ? [
              {
                resourceId: file.resourceId,
                name: buildReferencedFileName(file.displayName, file.extension),
                fileRevisionId: file.fileRevisionId,
              },
            ]
          : [],
      );
      if (uploaded.length > 0) {
        addReferencedFiles(uploaded);
        setAddedNotice(
          uploaded.length === 1
            ? `Added ${uploaded[0].name} to your workspace`
            : `Added ${uploaded.length} files to your workspace`,
        );
        if (addedTimerRef.current) clearTimeout(addedTimerRef.current);
        addedTimerRef.current = setTimeout(() => setAddedNotice(null), 3500);
      }
    },
  });
  const { isModalOpen, filesToUpload, handleCloseModal, handleUploadButtonClick, handleFileInputChange } =
    useFileUpload();

  const submit = () => {
    if (!canSend) return;
    onSend(trimmed);
    setDraft("");
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submit();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <div className="border-border shrink-0 border-t-[0.5px]">
      {referencedFiles.length > 0 ? (
        <div className="px-3 pt-2.5">
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span
              className={cn(
                "flex items-center gap-1 text-[11px] font-medium transition-colors",
                addedNotice ? "text-primary" : "text-muted-foreground",
              )}
            >
              <Check className="size-3 shrink-0" />
              {addedNotice ?? "In your workspace"}
            </span>
            <Button
              type="button"
              variant="link"
              size="2xs"
              onClick={clearReferencedFiles}
              className="text-muted-foreground h-auto px-0 hover:no-underline"
            >
              Clear all
            </Button>
          </div>
          <ul className="flex flex-wrap gap-1.5">
            {referencedFiles.map((file) => (
              <li
                key={file.resourceId}
                className="bg-accent flex max-w-full items-center gap-1.5 rounded-md py-1 pr-1 pl-2 text-xs"
              >
                <FileIcon className="text-muted-foreground size-3.5 shrink-0" />
                <Link
                  to={`${routes.Files}/${file.resourceId}`}
                  className="truncate underline-offset-2 hover:underline"
                  title={`Open ${file.name} in your workspace`}
                >
                  {file.name}
                </Link>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-2xs"
                      onClick={() => removeReferencedFile(file.resourceId)}
                      aria-label={`Remove ${file.name} from this chat`}
                      className="shrink-0"
                    >
                      <XIcon className="size-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">Remove from this chat — the file stays in your workspace</TooltipContent>
                </Tooltip>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <form onSubmit={handleSubmit} className="flex items-end gap-2 px-3 py-2.5">
        <label htmlFor={textareaId} className="sr-only">
          Message Istari AI
        </label>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={() => setAddFilesModalOpen(true)}
              disabled={isPending}
              aria-label="Upload files"
              className="size-8 shrink-0 rounded-full"
            >
              <PlusIcon className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Upload files</TooltipContent>
        </Tooltip>
        <textarea
          id={textareaId}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Message Istari AI…"
          rows={1}
          disabled={isPending}
          autoFocus
          className={cn(
            "placeholder:text-muted-foreground field-sizing-content flex-1 resize-none bg-transparent",
            "max-h-32 px-2 py-1.5 text-sm leading-snug outline-none",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        />
        <Button
          type="submit"
          size="icon-sm"
          disabled={!canSend}
          aria-label="Send message"
          className="size-8 shrink-0 rounded-full"
        >
          <ArrowUpIcon className="size-4" />
        </Button>
      </form>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileInputChange}
        className="hidden"
        aria-hidden="true"
      />
      <AddFilesModal
        open={addFilesModalOpen}
        onOpenChange={setAddFilesModalOpen}
        onUploadClick={() => handleUploadButtonClick(fileInputRef)}
        onConnectUpload={uploadFiles}
      />
      {isModalOpen && filesToUpload ? (
        <UploadSpecModal
          files={filesToUpload}
          open={isModalOpen}
          setModalOpen={handleCloseModal}
          onFilesUploaded={uploadFiles}
        />
      ) : null}
    </div>
  );
}
